import { Expander, ExternalLinkIcon, Label, ServicesIcon } from '@postidigital/posti-components'
import { observer } from 'mobx-react-lite'
import React, { FC, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { StoreContext } from '../../store'
import { breakpoint } from '../../utils'
import { ResponsiveLink } from '../typography/ResponsiveLink'
import { SERVICE_LIST_CONFIG } from './ServiceList.config'

const ServiceListComponent: FC = () => {
  const { t } = useTranslation()
  const [servicesOpen, setServicesOpen] = useState<boolean>(false)
  const {
    layoutStore: { isAppWebView },
    infoStore: { isConsumer, isCorporate },
  } = useContext(StoreContext)

  const serviceLinks = useMemo(
    () =>
      Object.values(SERVICE_LIST_CONFIG).map(({ labelTranslationId, link, service }) => (
        <StyledServiceLink key={service} icon={ExternalLinkIcon} iconPosition="right" href={link} target="_blank">
          {t(labelTranslationId)}
        </StyledServiceLink>
      )),
    [t]
  )

  if (isAppWebView || (isCorporate && !isConsumer)) {
    return null
  }

  return (
    <StyledExpander open={servicesOpen} onClick={() => setServicesOpen((isOpen) => !isOpen)}>
      <Expander.Title>
        <ServicesIcon />
        <Label size="Three" color="#3b4a57">
          {t('serviceLinkLabel.services')}
        </Label>
      </Expander.Title>
      <StyledExpanderBody $isOpen={servicesOpen}>{serviceLinks}</StyledExpanderBody>
    </StyledExpander>
  )
}

export default observer(ServiceListComponent)

const StyledServiceLink = styled(ResponsiveLink)`
  align-self: flex-start;
  display: inline-flex;
  gap: 0.5rem;
`

const StyledExpanderBody = styled(Expander.Body)<{ $isOpen: boolean }>(
  ({ $isOpen }) => css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-left: 3rem;
    ${$isOpen && 'padding-bottom: 1rem;'} @media ${breakpoint.mobile} {
      padding-left: 3.5rem;
    }
  `
)

const StyledExpander = styled(Expander)(
  ({ theme: { colorTokens } }) => css`
    margin-top: 1rem;
    overflow: hidden;
    background-color: transparent;
    border-radius: 2.5rem;
    --expander-border-radius: 2.5rem;

    & > button {
      padding: 0.75rem 1rem;
      background-color: transparent;
      border: 0;

      & > div {
        display: grid;
        grid-template-columns: 1.5rem minmax(0, 1fr);
        gap: 0.5rem;
        align-items: center;
      }

      &:focus-visible {
        outline: 2px solid ${colorTokens.postiOrange60};
        outline-offset: -2px;
      }

      &:focus-visible::before {
        content: none;
      }
    }

    & > div {
      background-color: transparent;
    }

    @media (hover: hover) {
      &:hover,
      &:hover > button,
      &:hover > div {
        background-color: #eaedf1;
      }
    }

    @media ${breakpoint.mobile} {
      margin-top: 0;
      overflow: visible;
      border-radius: 0;
      --expander-border-radius: 0;

      &,
      & > button,
      & > div,
      &:hover,
      &:hover > button,
      &:hover > div {
        background-color: transparent;
        border-radius: 0;
      }

      & > button {
        padding: 1rem 1.25rem;
        font-size: 1rem;
        font-weight: normal;

        & > div {
          gap: 0.75rem;
        }

        label {
          font-size: inherit;
          font-weight: inherit;
        }
      }
    }
  `
)
